// src/pages/Checkout.jsx
import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import toast from 'react-hot-toast';
import { useCartStore, useAuthStore } from '../context/store';
import api from '../utils/api';
import styles from './Checkout.module.css';

const STEPS = ['Livraison', 'Paiement', 'Confirmation'];

export default function Checkout() {
  const navigate   = useNavigate();
  const location   = useLocation();
  const { user }   = useAuthStore();
  const { items, subtotal, total, remise, codePromo, clearCart } = useCartStore();

  const [step, setStep]         = useState(0);
  const [adresse, setAdresse]   = useState(user?.adresse_ci || '');
  const [livraison, setLivraison] = useState(location.state?.livraison || 'domicile');
  const [methode, setMethode]   = useState('mtn');
  const [phone, setPhone]       = useState(user?.telephone || '');
  const [orderId, setOrderId]   = useState(null);
  const [orderRef, setOrderRef] = useState('');
  const [loading, setLoading]   = useState(false);
  const [payStatus, setPayStatus] = useState('idle');
  const [payMessage, setPayMessage] = useState('');
  const [countdown, setCountdown] = useState(0);
  const pollingRef = useRef(null);
  const countdownRef = useRef(null);

  const FRAIS = { domicile: 2000, collecte: 0, national: 5000 };
  const fraisLivraison = FRAIS[livraison] || 0;
  const totalFinal = total() + fraisLivraison;

  useEffect(() => {
    if (items.length === 0 && step !== 2) navigate('/panier');
  }, [items, step, navigate]);

  useEffect(() => () => {
    clearInterval(pollingRef.current);
    clearInterval(countdownRef.current);
  }, []);

  const createOrder = async () => {
    if (livraison === 'domicile' && !adresse.trim()) {
      toast.error('Veuillez renseigner votre adresse de livraison');
      return;
    }
    setLoading(true);
    try {
      const payload = {
        items: items.map(i => ({
          product_id: i.product.id,
          taille: i.taille,
          couleur: i.couleur,
          quantite: i.quantite
        })),
        livraison_type: livraison,
        adresse_livraison: adresse || null,
        code_promo: codePromo || undefined
      };
      const { data } = await api.post('/orders', payload);
      setOrderId(data.orderId);
      setOrderRef(data.ref);
      setStep(1);
      toast.success('Commande ' + data.ref + ' créée !');
    } catch (e) {
      toast.error(e.response?.data?.message || 'Erreur création commande');
    } finally {
      setLoading(false);
    }
  };

  const initPayment = async () => {
    if (!phone.trim()) { toast.error('Renseignez votre numéro de téléphone'); return; }
    setLoading(true);
    setPayStatus('idle');
    try {
      let res;
      if (methode === 'mtn') {
        res = await api.post('/payment/mtn', { order_id: orderId, phone });
      } else {
        res = await api.post('/payment/orange', { order_id: orderId });
        if (res.data.paymentUrl) window.open(res.data.paymentUrl, '_blank');
      }
      setPayStatus('waiting');
      setPayMessage(res.data.message || 'Confirmez le paiement sur votre téléphone...');
      let secs = 120;
      setCountdown(secs);
      countdownRef.current = setInterval(() => {
        secs -= 1;
        setCountdown(secs);
        if (secs <= 0) {
          clearInterval(countdownRef.current);
          clearInterval(pollingRef.current);
          setPayStatus('failed');
          setPayMessage('Le délai de paiement a expiré. Veuillez réessayer.');
        }
      }, 1000);
      pollingRef.current = setInterval(async () => {
        try {
          const { data } = await api.get('/payment/' + orderId + '/status');
          if (data.payment?.statut === 'success') {
            clearInterval(pollingRef.current);
            clearInterval(countdownRef.current);
            setPayStatus('success');
            setStep(2);
            clearCart();
          } else if (data.payment?.statut === 'failed') {
            clearInterval(pollingRef.current);
            clearInterval(countdownRef.current);
            setPayStatus('failed');
            setPayMessage('Paiement refusé. Vérifiez votre solde et réessayez.');
          }
        } catch {}
      }, 4000);
    } catch (e) {
      setPayStatus('failed');
      setPayMessage(e.response?.data?.message || 'Erreur lors du paiement');
      toast.error(e.response?.data?.message || 'Erreur paiement');
    } finally {
      setLoading(false);
    }
  };

  const retryPayment = () => {
    setPayStatus('idle');
    setPayMessage('');
    setCountdown(0);
    clearInterval(pollingRef.current);
    clearInterval(countdownRef.current);
  };

  const mtnSteps = ['Vérifiez votre téléphone MTN', 'Composez votre code PIN Mobile Money', 'Validez la transaction'];
  const orangeSteps = ['Finaliser sur la page Orange Money', 'Confirmer le paiement', 'Retournez ici après confirmation'];

  return (
    <main className={styles.page}>
      <div className={styles.inner}>
        <div className={styles.stepper}>
          {STEPS.map((s, i) => (
            <React.Fragment key={s}>
              <div className={[styles.stepItem, i <= step ? styles.stepDone : '', i === step ? styles.stepActive : ''].join(' ')}>
                <div className={styles.stepCircle}>{i < step ? '✓' : i + 1}</div>
                <span className={styles.stepLabel}>{s}</span>
              </div>
              {i < STEPS.length - 1 && (
                <div className={[styles.stepLine, i < step ? styles.stepLineDone : ''].join(' ')} />
              )}
            </React.Fragment>
          ))}
        </div>

        <div className={styles.layout}>
          <div className={styles.main}>

            {step === 0 && (
              <div className={styles.card}>
                <h2 className={styles.cardTitle}><span>📍</span> Informations de livraison</h2>
                <div className={styles.formGroup}>
                  <label className={styles.label}>Mode de livraison</label>
                  <div className={styles.livraisonGrid}>
                    {[
                      { id: 'domicile', label: 'Domicile Abidjan', price: 2000, icon: '🏠', sub: '24–48h' },
                      { id: 'collecte', label: 'Click & Collect', price: 0, icon: '🏪', sub: 'Même jour' },
                      { id: 'national', label: 'National CI', price: 5000, icon: '🚚', sub: '3–5 jours' },
                    ].map(l => (
                      <button key={l.id}
                        className={[styles.livraisonBtn, livraison === l.id ? styles.livraisonActive : ''].join(' ')}
                        onClick={() => setLivraison(l.id)}>
                        <span className={styles.livraisonIcon}>{l.icon}</span>
                        <div className={styles.livraisonInfo}>
                          <span className={styles.livraisonLabel}>{l.label}</span>
                          <span className={styles.livraisonSub}>{l.sub}</span>
                        </div>
                        <span className={styles.livraisonPrice}>
                          {l.price === 0 ? 'Gratuit' : l.price.toLocaleString('fr-CI') + ' FCFA'}
                        </span>
                      </button>
                    ))}
                  </div>
                </div>
                {(livraison === 'domicile' || livraison === 'national') && (
                  <div className={styles.formGroup}>
                    <label className={styles.label} htmlFor="adresse">Adresse de livraison *</label>
                    <textarea id="adresse" className={styles.textarea} value={adresse}
                      onChange={e => setAdresse(e.target.value)}
                      placeholder="Quartier, rue, description du lieu..." rows={3} />
                  </div>
                )}
                {livraison === 'collecte' && (
                  <div className={styles.collecteInfo}>
                    <span>🏪</span>
                    <div>
                      <p><strong>Point de retrait Fabi Luxe</strong></p>
                      <p>Abidjan, Cocody — vous recevrez l'adresse exacte par WhatsApp après confirmation.</p>
                    </div>
                  </div>
                )}
                <button className={styles.nextBtn} onClick={createOrder} disabled={loading}>
                  {loading ? <Spinner /> : 'Continuer vers le paiement →'}
                </button>
              </div>
            )}

            {step === 1 && (
              <div className={styles.card}>
                <h2 className={styles.cardTitle}><span>📱</span> Paiement Mobile Money</h2>
                {payStatus === 'idle' && (
                  <>
                    <div className={styles.formGroup}>
                      <label className={styles.label}>Méthode de paiement</label>
                      <div className={styles.payMethodGrid}>
                        {[
                          { id: 'mtn', name: 'MTN Mobile Money', sub: 'Paiement USSD direct', bg: '#FFF9C4', color: '#F57F17' },
                          { id: 'orange', name: 'Orange Money', sub: 'Redirection sécurisée', bg: '#FFE0CC', color: '#E65100' },
                        ].map(m => (
                          <button key={m.id}
                            className={[styles.payMethodBtn, methode === m.id ? styles.payMethodActive : ''].join(' ')}
                            onClick={() => setMethode(m.id)}>
                            <div className={styles.payMethodLogo} style={{ background: m.bg, color: m.color }}>
                              <span style={{ fontSize: 24 }}>📱</span>
                            </div>
                            <div>
                              <p className={styles.payMethodName}>{m.name}</p>
                              <p className={styles.payMethodSub}>{m.sub}</p>
                            </div>
                            {methode === m.id && <span className={styles.payMethodCheck}>✓</span>}
                          </button>
                        ))}
                      </div>
                    </div>
                    {methode === 'mtn' && (
                      <div className={styles.formGroup}>
                        <label className={styles.label} htmlFor="phone">Numéro MTN Mobile Money</label>
                        <div className={styles.phoneRow}>
                          <span className={styles.phonePrefix}>+225</span>
                          <input id="phone" className={styles.phoneInput} type="tel"
                            value={phone} onChange={e => setPhone(e.target.value)}
                            placeholder="07 XX XX XX XX" maxLength={12} />
                        </div>
                        <p className={styles.phoneHint}>
                          Vous recevrez une notification USSD pour confirmer le paiement de <strong>{totalFinal.toLocaleString('fr-CI')} FCFA</strong>
                        </p>
                      </div>
                    )}
                    {methode === 'orange' && (
                      <div className={styles.orangeInfo}>
                        <span>🔗</span>
                        <p>Vous serez redirigé vers Orange Money pour payer <strong>{totalFinal.toLocaleString('fr-CI')} FCFA</strong>.</p>
                      </div>
                    )}
                    <button className={styles.payBtn} onClick={initPayment} disabled={loading}>
                      {loading ? <Spinner /> : (
                        <>
                          <span>Payer {totalFinal.toLocaleString('fr-CI')} FCFA</span>
                          <span style={{ opacity: .7 }}>via {methode === 'mtn' ? 'MTN Money' : 'Orange Money'}</span>
                        </>
                      )}
                    </button>
                  </>
                )}

                {payStatus === 'waiting' && (
                  <div className={styles.payWaiting}>
                    <div className={styles.payWaitingAnim}>
                      <div className={styles.payWaitingRing} />
                      <span className={styles.payWaitingEmoji}>📱</span>
                    </div>
                    <h3 className={styles.payWaitingTitle}>En attente de confirmation</h3>
                    <p className={styles.payWaitingMsg}>{payMessage}</p>
                    <div className={styles.payCountdown}>
                      <div className={styles.payCountdownBar} style={{ width: (countdown / 120 * 100) + '%' }} />
                    </div>
                    <p className={styles.payCountdownText}>Expire dans <strong>{countdown}s</strong></p>
                    <div className={styles.paySteps}>
                      {(methode === 'mtn' ? mtnSteps : orangeSteps).map((s, i) => (
                        <div key={i} className={styles.payStep}>
                          <span className={styles.payStepNum}>{i + 1}</span>
                          <span>{s}</span>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {payStatus === 'failed' && (
                  <div className={styles.payFailed}>
                    <span className={styles.payFailedIcon}>❌</span>
                    <h3>Paiement non abouti</h3>
                    <p>{payMessage}</p>
                    <button className={styles.retryBtn} onClick={retryPayment}>↺ Réessayer le paiement</button>
                  </div>
                )}
              </div>
            )}

            {step === 2 && (
              <div className={[styles.card, styles.successCard].join(' ')}>
                <div className={styles.successAnim}>
                  <div className={styles.successCircle}>✓</div>
                </div>
                <h2 className={styles.successTitle}>Commande confirmée !</h2>
                <p className={styles.successRef}>Référence : <strong>{orderRef}</strong></p>
                <p className={styles.successMsg}>
                  Merci pour votre commande chez Fabi Luxe !<br />
                  Vous recevrez une confirmation par WhatsApp et SMS.
                </p>
                <div className={styles.successActions}>
                  <button className={styles.successWaBtn} onClick={() => {
                    const text = 'Bonjour Fabi Luxe ! Confirmation de ma commande ' + orderRef + '. Merci !';
                    window.open('https://wa.me/225XXXXXXXX?text=' + encodeURIComponent(text), '_blank');
                  }}>💬 Contacter via WhatsApp</button>
                  <button className={styles.successShopBtn} onClick={() => navigate('/boutique')}>Continuer les achats →</button>
                </div>
              </div>
            )}
          </div>

          {step < 2 && (
            <aside className={styles.recap}>
              <div className={styles.recapCard}>
                <h2 className={styles.recapTitle}>Récapitulatif</h2>
                <div className={styles.recapItems}>
                  {items.map(item => {
                    const prix = item.product.prix_promo || item.product.prix;
                    return (
                      <div key={item.key} className={styles.recapItem}>
                        <div className={styles.recapItemImg}>
                          {item.product.images?.[0] ? (
                            <img src={item.product.images[0]} alt={item.product.nom} />
                          ) : <span>🌺</span>}
                          <span className={styles.recapItemQty}>{item.quantite}</span>
                        </div>
                        <div className={styles.recapItemInfo}>
                          <p className={styles.recapItemName}>{item.product.nom}</p>
                          <p className={styles.recapItemOpts}>{item.taille}</p>
                        </div>
                        <span className={styles.recapItemPrice}>
                          {(prix * item.quantite).toLocaleString('fr-CI')} FCFA
                        </span>
                      </div>
                    );
                  })}
                </div>
                <div className={styles.recapTotaux}>
                  <div className={styles.recapRow}>
                    <span>Sous-total</span>
                    <span>{subtotal().toLocaleString('fr-CI')} FCFA</span>
                  </div>
                  {remise > 0 && (
                    <div className={[styles.recapRow, styles.recapDiscount].join(' ')}>
                      <span>Code {codePromo}</span>
                      <span>−{remise.toLocaleString('fr-CI')} FCFA</span>
                    </div>
                  )}
                  <div className={styles.recapRow}>
                    <span>Livraison</span>
                    <span>{fraisLivraison === 0 ? 'Gratuit' : fraisLivraison.toLocaleString('fr-CI') + ' FCFA'}</span>
                  </div>
                  <div className={[styles.recapRow, styles.recapTotal].join(' ')}>
                    <span>Total</span>
                    <span>{totalFinal.toLocaleString('fr-CI')} FCFA</span>
                  </div>
                </div>
              </div>
            </aside>
          )}
        </div>
      </div>
    </main>
  );
}

const Spinner = () => (
  <div style={{ width: 18, height: 18, border: '2px solid rgba(255,255,255,.3)', borderTopColor: 'currentColor', borderRadius: '50%', animation: 'spin .7s linear infinite', flexShrink: 0 }} />
);
